import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { linksAPI, projectsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, Trash2, Link2, Copy, Share2, Mail, Clock, ExternalLink, Phone
} from 'lucide-react';
import { toast } from 'sonner';

const EXPIRY_OPTIONS = [
  { value: '24h', label: '24 Horas' },
  { value: '3d', label: '3 Dias' },
  { value: '7d', label: '7 Dias' },
  { value: '30d', label: '30 Dias' },
  { value: '90d', label: '90 Dias' },
  { value: '6m', label: '6 Meses' },
  { value: '1y', label: '1 Ano' },
  { value: 'unlimited', label: 'Tempo Indeterminado' },
];

const DEFAULT_IFRAMES = [
  { url: "https://whep.flue.live/?stream=JJrpa001", name: "JJrpa001" },
  { url: "https://whep.flue.live/?stream=JJrpa002", name: "JJrpa002" },
  { url: "https://whep.flue.live/?stream=JJrpa003", name: "JJrpa003" },
  { url: "https://perry.flue.live/?stream=JJrpa001&record", name: "JJrpa001 (Rec)" },
  { url: "https://whep.flue.live/?stream=JORGEX2001", name: "JORGEX2001" },
  { url: "https://whep.flue.live/?stream=JORGEX2002", name: "JORGEX2002" },
  { url: "https://whep.flue.live/?stream=JORGEX2003", name: "JORGEX2003" },
  { url: "https://perry.flue.live/?stream=JORGEX2001&record", name: "JORGEX2001 (Rec)" }
];

const LinksPage = () => {
  const { isSuperAdmin, user } = useAuth();
  const [links, setLinks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [iframeUrl, setIframeUrl] = useState('');
  const [linkName, setLinkName] = useState('');
  const [expiryOption, setExpiryOption] = useState('7d');

  const fetchData = async () => {
    try {
      const [linksRes, projectsRes] = await Promise.all([
        linksAPI.list(),
        isSuperAdmin ? projectsAPI.list() : Promise.resolve({ data: [] })
      ]);
      setLinks(linksRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const resetForm = () => {
    setProjectId(user?.project_id || '');
    setIframeUrl('');
    setLinkName('');
    setExpiryOption('7d');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await linksAPI.create({
        project_id: isSuperAdmin ? projectId : user.project_id,
        iframe_url: iframeUrl,
        name: linkName,
        expiry_option: expiryOption
      });
      toast.success('Link criado com sucesso!');
      setCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar link');
    }
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm('Tem certeza que deseja excluir este link?')) return;
    try {
      await linksAPI.delete(linkId);
      toast.success('Link excluido com sucesso!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir link');
    }
  };

  const getShareUrl = (link) => `${window.location.origin}/stream/${link.access_token}`;

  const copyLink = (link) => {
    navigator.clipboard.writeText(getShareUrl(link));
    toast.success('Link copiado!');
  };

  const shareWhatsApp = (link) => {
    const url = encodeURIComponent(getShareUrl(link));
    const text = encodeURIComponent(`Acesse o stream: ${link.name}`);
    window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
  };

  const shareEmail = (link) => {
    const subject = encodeURIComponent(`Stream RPAS: ${link.name}`);
    const body = encodeURIComponent(`Acesse o stream atraves do link:\n\n${getShareUrl(link)}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return 'Sem expiracao';
    const diff = new Date(expiresAt) - new Date();
    if (diff <= 0) return 'Expirado';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days}d ${hours}h restantes`;
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return hours > 0 ? `${hours}h ${minutes}m restantes` : `${minutes}m restantes`;
  };

  const isExpired = (expiresAt) => expiresAt && new Date(expiresAt) < new Date();

  const getProjectName = (id) => projects.find(p => p.id === id)?.name || 'Projeto';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="links-page">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-rajdhani font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2">
          <Link2 size={20} className="text-gold" />
          GERADOR DE LINKS
        </h2>
        
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" onClick={() => { resetForm(); setCreateOpen(true); }} data-testid="create-link-btn">
              <Plus size={18} className="mr-2" />
              NOVO LINK
            </Button>
          </DialogTrigger>
          
          <DialogContent className="bg-white border border-gray-200 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-gray-900 font-rajdhani uppercase tracking-wider">
                CRIAR LINK PERSONALIZADO
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {isSuperAdmin && (
                <div className="space-y-2">
                  <Label className="text-gray-600 font-mono text-xs uppercase">Projeto</Label>
                  <Select value={projectId} onValueChange={setProjectId} required>
                    <SelectTrigger className="input-hud" data-testid="link-project-select">
                      <SelectValue placeholder="Selecione o projeto" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-gray-600 font-mono text-xs uppercase">Nome do Link</Label>
                <Input value={linkName} onChange={(e) => setLinkName(e.target.value)} className="input-hud" placeholder="Ex: Stream Principal" required data-testid="link-name-input" />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-600 font-mono text-xs uppercase">Stream</Label>
                <Select value={iframeUrl} onValueChange={setIframeUrl} required>
                  <SelectTrigger className="input-hud" data-testid="link-iframe-select">
                    <SelectValue placeholder="Selecione o stream" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEFAULT_IFRAMES.map((iframe, index) => (
                      <SelectItem key={index} value={iframe.url}>{iframe.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-600 font-mono text-xs uppercase">Validade</Label>
                <Select value={expiryOption} onValueChange={setExpiryOption}>
                  <SelectTrigger className="input-hud" data-testid="link-expiry-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPIRY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="btn-secondary flex-1">CANCELAR</Button>
                <Button type="submit" className="btn-primary flex-1" data-testid="save-link-btn">CRIAR LINK</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Links Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {links.map((link, idx) => (
          <div 
            key={link.id} 
            className={`card-hud p-5 space-y-4 animate-slide-up ${isExpired(link.expires_at) ? 'opacity-60' : ''}`}
            style={{ animationDelay: `${idx * 0.05}s` }}
            data-testid={`link-card-${link.id}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-gray-900 font-rajdhani font-semibold uppercase">{link.name}</h3>
                <p className="text-gray-400 text-xs font-mono mt-1">
                  {link.iframe_url.split('stream=')[1]?.split('&')[0] || 'Stream'}
                </p>
              </div>
            </div>

            <div className={`flex items-center gap-2 ${isExpired(link.expires_at) ? 'text-red-500' : 'text-blue-600'}`}>
              <Clock size={14} />
              <span className="text-xs font-mono">{getTimeRemaining(link.expires_at)}</span>
              {isExpired(link.expires_at) && <span className="badge-danger">EXPIRADO</span>}
            </div>

            {/* Copy */}
            {!isExpired(link.expires_at) && (
              <div className="flex gap-2">
                <Button onClick={() => copyLink(link)} className="btn-secondary flex-1 text-xs" data-testid={`copy-link-${link.id}`}>
                  <Copy size={14} className="mr-1" /> COPIAR
                </Button>
                <Button onClick={() => window.open(getShareUrl(link), '_blank')} variant="ghost" size="icon" className="text-blue-600 hover:text-gold hover:bg-gold/10">
                  <ExternalLink size={16} />
                </Button>
              </div>
            )}

            {/* Share: WhatsApp + Email + Delete */}
            {!isExpired(link.expires_at) && (
              <div className="flex gap-2 pt-3 border-t border-gray-100">
                <Button onClick={() => shareWhatsApp(link)} className="flex-1 bg-green-50 border border-green-200 text-green-700 hover:bg-green-100 text-xs rounded-lg" data-testid={`whatsapp-link-${link.id}`}>
                  <Phone size={14} className="mr-1" /> WhatsApp
                </Button>
                <Button onClick={() => shareEmail(link)} className="flex-1 bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 text-xs rounded-lg" data-testid={`email-link-${link.id}`}>
                  <Mail size={14} className="mr-1" /> E-mail
                </Button>
                <Button onClick={() => handleDelete(link.id)} variant="ghost" size="icon" className="text-gray-400 hover:text-red-500 hover:bg-red-50" data-testid={`delete-link-${link.id}`}>
                  <Trash2 size={16} />
                </Button>
              </div>
            )}

            {isExpired(link.expires_at) && (
              <div className="flex justify-end">
                <Button onClick={() => handleDelete(link.id)} className="btn-danger text-xs" data-testid={`delete-link-${link.id}`}>
                  <Trash2 size={14} className="mr-1" /> EXCLUIR
                </Button>
              </div>
            )}

            {isSuperAdmin && (
              <div className="text-gray-400 text-xs font-mono pt-2 border-t border-gray-100">
                Projeto: {getProjectName(link.project_id)}
              </div>
            )}
          </div>
        ))}
      </div>

      {links.length === 0 && (
        <div className="card-hud p-8 text-center">
          <Link2 size={48} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-mono">Nenhum link criado</p>
          <Button className="btn-primary mt-4" onClick={() => { resetForm(); setCreateOpen(true); }}>
            <Plus size={18} className="mr-2" /> CRIAR PRIMEIRO LINK
          </Button>
        </div>
      )}
    </div>
  );
};

export default LinksPage;
